import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  CopyButton,
  Divider,
  Drawer,
  Flex,
  Group,
  LoadingOverlay,
  Paper,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import dayjs from 'dayjs';
import {
  PiArrowSquareOut,
  PiBuildings,
  PiCalendar,
  PiCheck,
  PiCopy,
  PiDownloadSimple,
  PiInfo,
  PiLightning,
  PiMoney,
  PiPaperPlaneTilt,
  PiPencilSimple,
  PiUser,
  PiWarning,
  PiX,
} from 'react-icons/pi';
import { Link, useLocation } from 'wouter';
import { formatDate } from '@/lib/helpers';

const formatDateTime = (v: string | null | undefined) =>
  v ? dayjs(v).format('D MMM YYYY HH:mm') : '';

import {
  isMockInvoice,
  isUpcomingInvoice,
  mergeMockInvoices,
} from '@/lib/mockInvoiceMerge';
import { MONDAY_BOARDS, mondayItemUrl } from '@/lib/monday';
import { useHasAnyPermission } from '@/models/permissions';
import { useMockSplitSchedule } from '@/pages/offers/mock/mockSplitSchedule';
import {
  InvoiceDetails,
  useDownloadDocument,
  useDownloadInvoicePDF,
  useInvoiceDetails,
  useInvoiceDetailsByOfferId,
  useSendInvoice,
} from '../../models/invoices';
import {
  CorporateClientDetails,
  IndividualClientDetails,
  InvoiceStatus,
  PaymentMethod,
  TInvoice,
  TInvoiceOpportunityStatus,
} from '../../types';
import { StatusBadge } from '../StatusBadge';

interface InvoiceDetailsModalProps {
  invoiceId: string | null;
  opened: boolean;
  onClose: () => void;
  offerId?: number;
  // Render from a provided invoice instead of fetching (mock installment rows).
  invoiceOverride?: InvoiceDetails;
  onDownloadPDF?: (invoice: TInvoice) => void;
  onCancelInvoice?: (invoice: TInvoice) => void;
  onSettle?: (invoice: TInvoice) => void;
  onRecordMovement?: (invoice: TInvoice) => void;
  onProvidePaymentConfirmation?: (invoice: TInvoice) => void;
  onMondayOpportunityCreate?: (invoice: TInvoice) => void;
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Paper withBorder p="sm">
      <Group gap="xs" mb="xs">
        {icon}
        <Text size="sm" fw={500}>
          {title}
        </Text>
      </Group>
      <Divider mb="xs" />
      {children}
    </Paper>
  );
}

function PaymentInfo({ invoice }: { invoice: InvoiceDetails }) {
  const hasPaymentProof =
    invoice.paymentConfirmationHash || invoice.paymentConfirmationFileId;
  const downloadDocument = useDownloadDocument();

  return (
    <Stack gap={4}>
      {/* Which entity issued this invoice, frozen on it at issuance. The page
          named the paying party but never the seller, so an accountant could
          not tell whose terms - or whose VAT treatment - the document states. */}
      {invoice.issuer && (
        <Group gap="xs" align="flex-start">
          <Text size="xs" c="dimmed" w={80}>
            Issued by:
          </Text>
          <Box style={{ flex: 1 }}>
            <Text size="xs">{invoice.issuer.name}</Text>
            <Text size="xs" c="dimmed">
              {invoice.issuer.countryCode}
              {invoice.issuer.vatNumber
                ? ` · VAT ${invoice.issuer.vatNumber}`
                : invoice.issuer.vatRegime === 'none'
                  ? ' · levies no VAT'
                  : ''}
            </Text>
          </Box>
        </Group>
      )}

      <Group gap="xs">
        <Text size="xs" c="dimmed" w={80}>
          Currency:
        </Text>
        <Text size="xs">{invoice.dueCurrency}</Text>
      </Group>

      {invoice.paymentMethod && (
        <Group gap="xs">
          <Text size="xs" c="dimmed" w={80}>
            Method:
          </Text>
          <Badge variant="outline" size="xs" color="gray">
            {invoice.paymentMethod}
          </Badge>
        </Group>
      )}

      {invoice.paymentMethod === PaymentMethod.Cryptocurrency &&
        !hasPaymentProof && (
          <Alert color="gray" p="xs">
            <Text size="xs">Awaiting payment confirmation</Text>
          </Alert>
        )}

      {hasPaymentProof && (
        <Alert color="gray" p="xs">
          <Text size="xs" fw={500}>
            ✓ Payment confirmation provided
          </Text>
        </Alert>
      )}

      {invoice.paymentConfirmationHash && (
        <Paper withBorder p="xs" bg="var(--mantine-color-gray-light)">
          <Group gap="xs" justify="space-between">
            <div style={{ flex: 1 }}>
              <Text size="xs" c="dimmed" mb={2}>
                Transaction Hash:
              </Text>
              <Text
                size="xs"
                style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
              >
                {invoice.paymentConfirmationHash}
              </Text>
            </div>
            <CopyButton value={invoice.paymentConfirmationHash}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? 'Copied!' : 'Copy hash'}>
                  <Button
                    size="xs"
                    variant="subtle"
                    color="gray"
                    onClick={copy}
                    leftSection={<PiCopy size={10} />}
                  >
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </Tooltip>
              )}
            </CopyButton>
          </Group>
        </Paper>
      )}

      {invoice.paymentConfirmationFileId && (
        <Paper withBorder p="xs" bg="var(--mantine-color-gray-light)">
          <Group gap="xs" justify="space-between">
            <div>
              <Text size="xs" c="dimmed">
                Payment Confirmation File:
              </Text>
              <Text size="xs">Uploaded document</Text>
            </div>
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              leftSection={<PiDownloadSimple size={10} />}
              loading={downloadDocument.isPending}
              onClick={() =>
                downloadDocument.mutate(invoice.paymentConfirmationFileId!)
              }
            >
              Download
            </Button>
          </Group>
        </Paper>
      )}
    </Stack>
  );
}

// Who is being billed, in the detail the printed document leaves out. The name is the
// card's title rather than a field, because BILL TO on the document already states it.
function PayerCard({ invoice }: { invoice: InvoiceDetails }) {
  const clientInput = invoice.clientInput;
  if (!clientInput) return null;
  const name =
    clientInput.clientType === 'Individual'
      ? (clientInput.details as IndividualClientDetails).fullName
      : (clientInput.details as CorporateClientDetails).companyName;

  return (
    <Paper withBorder p="sm">
      <Group gap="xs" mb="xs" justify="space-between" wrap="nowrap">
        <Group gap="xs" wrap="nowrap">
          <PiUser size={14} />
          <Text size="sm" fw={500}>
            {name || 'Payer'}
          </Text>
        </Group>
        <Badge
          variant="light"
          size="xs"
          color={clientInput.clientType === 'Individual' ? 'blue' : 'teal'}
        >
          {clientInput.clientType}
        </Badge>
      </Group>
      <Divider mb="xs" />
      <Stack gap={4}>
        {clientInput.clientType === 'Individual' ? (
          <>
            {(clientInput.details as IndividualClientDetails)
              .documentNumber && (
              <Group gap="xs">
                <Text size="xs" c="dimmed" w={80}>
                  Document:
                </Text>
                <Text size="xs">
                  {
                    (clientInput.details as IndividualClientDetails)
                      .documentNumber
                  }
                </Text>
              </Group>
            )}
            {(clientInput.details as IndividualClientDetails).nationality && (
              <Group gap="xs">
                <Text size="xs" c="dimmed" w={80}>
                  Nationality:
                </Text>
                <Text size="xs">
                  {(clientInput.details as IndividualClientDetails).nationality}
                </Text>
              </Group>
            )}
            {(clientInput.details as IndividualClientDetails)
              .residenceCountry && (
              <Group gap="xs">
                <Text size="xs" c="dimmed" w={80}>
                  Residence:
                </Text>
                <Text size="xs">
                  {
                    (clientInput.details as IndividualClientDetails)
                      .residenceCountry
                  }
                </Text>
              </Group>
            )}
            {(clientInput.details as IndividualClientDetails).legalAddress && (
              <Group gap="xs" align="flex-start">
                <Text size="xs" c="dimmed" w={80}>
                  Address:
                </Text>
                <Text size="xs" style={{ flex: 1 }}>
                  {
                    (clientInput.details as IndividualClientDetails)
                      .legalAddress
                  }
                </Text>
              </Group>
            )}
          </>
        ) : (
          <>
            {(clientInput.details as CorporateClientDetails)
              .companyRegNumber && (
              <Group gap="xs">
                <Text size="xs" c="dimmed" w={80}>
                  Reg. Number:
                </Text>
                <Text size="xs">
                  {
                    (clientInput.details as CorporateClientDetails)
                      .companyRegNumber
                  }
                </Text>
              </Group>
            )}
            {(clientInput.details as CorporateClientDetails).companyTaxId && (
              <Group gap="xs">
                <Text size="xs" c="dimmed" w={80}>
                  Tax ID:
                </Text>
                <Text size="xs">
                  {(clientInput.details as CorporateClientDetails).companyTaxId}
                </Text>
              </Group>
            )}
            {(clientInput.details as CorporateClientDetails)
              .companyLegalAddress && (
              <Group gap="xs" align="flex-start">
                <Text size="xs" c="dimmed" w={80}>
                  Address:
                </Text>
                <Text size="xs" style={{ flex: 1 }}>
                  {
                    (clientInput.details as CorporateClientDetails)
                      .companyLegalAddress
                  }
                </Text>
              </Group>
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
}

function StatusHistory({ invoice }: { invoice: InvoiceDetails }) {
  if (!invoice.statusHistory || invoice.statusHistory.length <= 0) return null;

  const formatStatusName = (status: InvoiceStatus): string => {
    switch (status) {
      case InvoiceStatus.ISSUED:
        return 'Issued';
      case InvoiceStatus.CONFIRMED_BY_CLIENT:
        return 'Confirmed by Client';
      case InvoiceStatus.PAID:
        return 'Paid';
      case InvoiceStatus.PARTIALLY_PAID:
        return 'Partially Paid';
      case InvoiceStatus.CANCELLED:
        return 'Cancelled';
      default:
        return status;
    }
  };

  const sortedHistory = [...invoice.statusHistory].sort(
    (a, b) => new Date(a.changedAt).getTime() - new Date(b.changedAt).getTime(),
  );

  return (
    <Stack gap="xs">
      {sortedHistory.slice(0, 3).map((entry, index) => {
        return (
          <Flex key={index} gap="xs" justify="space-between" align={'center'}>
            <Text size="xs" c="dimmed">
              <Text component="span" fw={500} c="bright">
                {formatStatusName(entry.oldStatus)}
              </Text>
              {' → '}
              <Text component="span" fw={500} c="bright">
                {formatStatusName(entry.newStatus)}
              </Text>
            </Text>
            <Stack gap={0} ta="right">
              <Text size="xs" c="dimmed">
                {entry.changedByEmail ?? 'Client'}
              </Text>
              <Text size="xs" c="dimmed">
                {dayjs(entry.changedAt).format('HH:mm DD.MM.YYYY')}
              </Text>
            </Stack>
          </Flex>
        );
      })}
      {sortedHistory.length > 3 && (
        <Text size="xs" c="dimmed">
          +{sortedHistory.length - 3} more entries
        </Text>
      )}
    </Stack>
  );
}

function StatusSpecificActions({
  invoice,
  onDownloadPDF,
  onCancelInvoice,
  onSettle,
  onRecordMovement,
  onProvidePaymentConfirmation,
  onMondayOpportunityCreate,
  onAmend,
}: {
  invoice: InvoiceDetails;
  onDownloadPDF?: (invoice: TInvoice) => void;
  onCancelInvoice?: (invoice: TInvoice) => void;
  onSettle?: (invoice: TInvoice) => void;
  onRecordMovement?: (invoice: TInvoice) => void;
  onProvidePaymentConfirmation?: (invoice: TInvoice) => void;
  onMondayOpportunityCreate?: (invoice: TInvoice) => void;
  onAmend?: (invoice: InvoiceDetails) => void;
}) {
  // Declaring money received is Accounting's. Sales staff can open an invoice
  // belonging to one of their offers, so without this they would be shown
  // controls the server refuses.
  const mayMoveMoney = useHasAnyPermission([
    'invoices:update-status',
    'invoices:*',
    '*:*',
  ]);
  const hasPaymentProof = !!(
    invoice.paymentConfirmationHash || invoice.paymentConfirmationFileId
  );
  const settled =
    invoice.status === InvoiceStatus.PAID ||
    invoice.status === InvoiceStatus.CANCELLED;
  // Whether an opportunity exists is shown; creating another stays available on top of
  // that. An opportunity deleted in Monday has to be re-made from here, so the button
  // is deliberately not hidden once one has been created - only while one is in flight,
  // where a second press would race the queue rather than replace a missing item.
  const opportunityBusy =
    invoice.opportunityStatus === TInvoiceOpportunityStatus.Processing ||
    invoice.opportunityStatus === TInvoiceOpportunityStatus.Queued;
  const opportunityCreated =
    invoice.opportunityStatus === TInvoiceOpportunityStatus.Created &&
    invoice.opportunityId != null;
  const opportunityFailed =
    invoice.opportunityStatus === TInvoiceOpportunityStatus.Failed;
  const canCreateOpportunity =
    !opportunityBusy &&
    (invoice.status === InvoiceStatus.PAID ||
      invoice.status === InvoiceStatus.PARTIALLY_PAID);

  return (
    <Section title="Actions" icon={<PiLightning size={14} />}>
      {/* Every action this invoice allows is shown outright. A "more" menu hid the
          ones people actually came for - creating the opportunity above all - behind
          a click that gave no clue what was inside it. */}
      <Group gap="xs" wrap="wrap">
        {/* Attaching the confirmation and marking paid were two buttons calling the
            same handler; they are one step with two stages now. */}
        {mayMoveMoney &&
          !settled &&
          (hasPaymentProof ? (
            <Button
              size="xs"
              color="teal"
              leftSection={<PiCheck size={12} />}
              onClick={() => onSettle?.(invoice)}
            >
              Settle invoice
            </Button>
          ) : (
            <Button
              size="xs"
              leftSection={<PiMoney size={12} />}
              onClick={() => onProvidePaymentConfirmation?.(invoice)}
            >
              Confirm payment
            </Button>
          ))}

        <Button
          size="xs"
          variant="default"
          leftSection={<PiDownloadSimple size={12} />}
          onClick={() => onDownloadPDF?.(invoice)}
        >
          Download PDF
        </Button>

        {/* A settled invoice still accepts movements - its status is final, its history is
            not - so this one is not gated on `settled`. */}
        {mayMoveMoney && (
          <Button
            size="xs"
            variant="default"
            leftSection={<PiPencilSimple size={12} />}
            onClick={() => onRecordMovement?.(invoice)}
          >
            Record payment
          </Button>
        )}

        {invoice.status === InvoiceStatus.ISSUED && onAmend && (
          <Button
            size="xs"
            variant="default"
            leftSection={<PiPencilSimple size={12} />}
            onClick={() => onAmend(invoice)}
          >
            Amend invoice
          </Button>
        )}

        {canCreateOpportunity && (
          <Button
            size="xs"
            variant="default"
            leftSection={<PiLightning size={12} />}
            onClick={() => onMondayOpportunityCreate?.(invoice)}
          >
            {opportunityFailed
              ? 'Retry Monday opportunity'
              : opportunityCreated
                ? 'Create again'
                : 'Create Monday opportunity'}
          </Button>
        )}

        {opportunityBusy && (
          // Creating is queued work, so the control owns the wait: the state is
          // persisted, so it survives a reload, and the details query polls until the
          // queue resolves it.
          <Button size="xs" variant="default" loading disabled>
            Creating opportunity…
          </Button>
        )}

        {opportunityCreated && (
          <Button
            size="xs"
            variant="light"
            component="a"
            href={mondayItemUrl(
              MONDAY_BOARDS.OPPORTUNITIES,
              invoice.opportunityId!,
            )}
            target="_blank"
            rel="noreferrer"
            leftSection={<PiArrowSquareOut size={12} />}
          >
            Opportunity in Monday
          </Button>
        )}

        {opportunityFailed && (
          <Text fz="xs" c="red">
            The last opportunity attempt failed.
          </Text>
        )}

        {invoice.status === InvoiceStatus.ISSUED && (
          <Button
            size="xs"
            variant="default"
            color="red"
            leftSection={<PiX size={12} />}
            onClick={() => onCancelInvoice?.(invoice)}
          >
            Cancel invoice
          </Button>
        )}
      </Group>
    </Section>
  );
}

function OfferContextSection({ invoice }: { invoice: InvoiceDetails }) {
  const [, navigate] = useLocation();
  const offerId = invoice.offerId;
  const { data: realSiblings = [] } = useInvoiceDetailsByOfferId(offerId ?? 0);
  // Mix in the mock split invoices: cancelled original + issued installments,
  // plus upcoming (scheduled) installments so the accountant can preview them.
  const mockSchedules = useMockSplitSchedule((s) => s.schedules);
  const siblings = mergeMockInvoices(realSiblings, mockSchedules, {
    includeUpcoming: true,
  }) as InvoiceDetails[];
  if (!offerId) return null;

  const splitActive = siblings.some((s) => s.partNumber != null);
  // A one-row table restating the invoice already open on this page is noise; the
  // link to the offer is the whole point of the card then.
  const showSiblings = siblings.length > 1;
  if (!showSiblings) return null;

  return (
    <Paper withBorder p="sm">
      <Stack gap="sm">
        <Group justify="space-between">
          <Group gap={6}>
            <Text size="sm" c="dimmed">
              Offer
            </Text>
            <Anchor
              component={Link}
              href={`~/offers/${offerId}/general`}
              size="sm"
              fw={500}
            >
              {invoice.offerTitle || `Offer #${offerId}`}
            </Anchor>
          </Group>
          {splitActive && (
            <Badge color="indigo" variant="light">
              Split committed
            </Badge>
          )}
        </Group>

        {showSiblings && (
          <div>
            <Text size="xs" c="dimmed" mb={4}>
              {splitActive
                ? `Payment plan · ${siblings.length} parts`
                : `Invoices for this offer (${siblings.length})`}
            </Text>
            <Table
              withTableBorder
              verticalSpacing="xs"
              fz="xs"
              highlightOnHover
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Invoice</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th ta="right">Paid / Due</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {siblings.map((s) => {
                  const isCurrent = s.id === invoice.id;
                  const upcoming = isUpcomingInvoice(s);
                  return (
                    <Table.Tr
                      key={s.id}
                      bg={
                        isCurrent
                          ? 'var(--mantine-color-blue-light)'
                          : undefined
                      }
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`~/invoices/${s.id}`)}
                    >
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          <Text
                            size="xs"
                            fw={500}
                            ff="monospace"
                            c={upcoming ? 'dimmed' : undefined}
                          >
                            {upcoming ? '—' : s.invoiceNumber}
                          </Text>
                          {s.partNumber != null && (
                            <Badge size="xs" variant="light" color="indigo">
                              Part {s.partNumber}
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        {isCurrent ? (
                          <Text size="xs" c="dimmed">
                            this invoice
                          </Text>
                        ) : upcoming ? (
                          <Badge size="sm" variant="light" color="gray">
                            Scheduled
                          </Badge>
                        ) : (
                          <StatusBadge status={s.status} size="sm" />
                        )}
                      </Table.Td>
                      <Table.Td ta="right" style={{ whiteSpace: 'nowrap' }}>
                        {(s.paidAmount || 0).toFixed(2)} /{' '}
                        {(s.dueAmount || 0).toFixed(2)} {s.dueCurrency}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </div>
        )}
      </Stack>
    </Paper>
  );
}

const money2 = (a: number | null | undefined, c: string | null | undefined) =>
  a != null
    ? `${Number(a).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} ${c || ''}`
    : '';

// PDF-invoice-style line items: grouped by block, parents numbered, child
// services indented; optional "covered" column when viewing a split installment.
// A split installment bills a portion of the order, so its document lists the whole
// order grouped by the installment covering each service, with amounts stated per
// installment and never per line. This mirrors the customer's PDF exactly.
function InstallmentItemsSection({ invoice }: { invoice: InvoiceDetails }) {
  const groups = invoice.installments?.groups ?? [];
  const rows: React.ReactNode[] = [];

  for (const g of groups) {
    rows.push(
      <Table.Tr key={`g-${g.seq ?? 'rest'}`}>
        <Table.Td colSpan={2} pt="md">
          <Text fw={700} fz="sm">
            {g.label}
          </Text>
          {g.note && (
            <Text fz="xs" c="dimmed">
              {g.note}
            </Text>
          )}
        </Table.Td>
        <Table.Td ta="right" pt="md" style={{ whiteSpace: 'nowrap' }}>
          {g.amount != null && (
            <Text fw={700} fz="sm">
              {money2(g.amount, g.currency ?? invoice.dueCurrency)}
            </Text>
          )}
        </Table.Td>
      </Table.Tr>,
    );
    for (const s of g.services) {
      if (s.groupHead) {
        rows.push(
          <Table.Tr key={`h-${g.seq}-${s.groupHead}-${s.i}`}>
            <Table.Td colSpan={3}>
              <Text fz="xs" c="dimmed">
                {s.groupHead}
              </Text>
            </Table.Td>
          </Table.Tr>,
        );
      }
      rows.push(
        <Table.Tr key={`s-${g.seq}-${s.i}-${s.description}`}>
          <Table.Td ta="right" w={36}>
            {s.i}
          </Table.Td>
          <Table.Td>
            <Text
              fz="sm"
              pl={s.isChild ? 'md' : 0}
              c={s.isChild ? 'dimmed' : undefined}
            >
              {s.isChild ? `- ${s.description}` : s.description}
            </Text>
          </Table.Td>
          <Table.Td ta="center" w={50}>
            {s.isChild ? '' : 1}
          </Table.Td>
        </Table.Tr>,
      );
    }
  }

  return (
    <Table verticalSpacing={6} fz="sm">
      <Table.Thead>
        <Table.Tr>
          <Table.Th ta="right">Nr</Table.Th>
          <Table.Th>Description</Table.Th>
          <Table.Th ta="center">Qty</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>{rows}</Table.Tbody>
    </Table>
  );
}

function LineItemsSection({ invoice }: { invoice: InvoiceDetails }) {
  const offerId = invoice.offerId;
  const { data: siblings = [] } = useInvoiceDetailsByOfferId(offerId ?? 0);
  const mockSchedules = useMockSplitSchedule((s) => s.schedules);

  const original = siblings.find((s) => s.partNumber == null);
  const positions = invoice.positions?.length
    ? invoice.positions
    : (original?.positions ?? []);
  if (positions.length === 0) return null;

  const sched = offerId != null ? mockSchedules[offerId] : undefined;
  let covered: Set<number> | null = null;
  if (sched?.status === 'approved' && invoice.partNumber != null) {
    const inst = sched.installments[invoice.partNumber - 1];
    if (inst) {
      covered = new Set(
        Object.entries(sched.assignment)
          .filter(([, v]) => v === inst.id)
          .map(([k]) => Number(k.split('-').pop()))
          .filter((n) => !Number.isNaN(n)),
      );
    }
  }
  const showCovered = covered != null;
  const colSpan = showCovered ? 5 : 4;
  const isCovered = (p: {
    serviceId: number;
    serviceParentId: number | null;
  }) =>
    covered != null &&
    (covered.has(p.serviceId) ||
      (p.serviceParentId != null && covered.has(p.serviceParentId)));

  const rows: React.ReactNode[] = [];
  let lastBlock: string | null = null;
  let nr = 0;
  for (const p of positions) {
    const block = p.blockTitle || '';
    if (block !== lastBlock) {
      lastBlock = block;
      if (block) {
        rows.push(
          <Table.Tr key={`block-${p.id}`}>
            <Table.Td colSpan={colSpan} pt="sm">
              <Text fw={700} fz="sm">
                {block}
              </Text>
            </Table.Td>
          </Table.Tr>,
        );
      }
    }
    const isChild = p.serviceParentId != null;
    if (!isChild) nr += 1;
    const cov = isCovered(p);
    rows.push(
      <Table.Tr key={p.id} style={{ opacity: showCovered && !cov ? 0.45 : 1 }}>
        <Table.Td ta="right" w={36}>
          {isChild ? '' : nr}
        </Table.Td>
        <Table.Td>
          <Text
            fz="sm"
            pl={isChild ? 'md' : 0}
            c={isChild ? 'dimmed' : undefined}
          >
            {isChild ? `- ${p.serviceName}` : p.serviceName}
          </Text>
        </Table.Td>
        {showCovered && (
          <Table.Td ta="center" w={90}>
            {cov ? (
              <PiCheck size={14} color="var(--mantine-color-teal-6)" />
            ) : (
              ''
            )}
          </Table.Td>
        )}
        <Table.Td ta="center" w={50}>
          {isChild ? '' : p.quantity}
        </Table.Td>
        <Table.Td ta="right" w={140} style={{ whiteSpace: 'nowrap' }}>
          {isChild ? '' : money2(p.priceAmount, p.priceCurrency)}
        </Table.Td>
      </Table.Tr>,
    );
  }

  return (
    <Table verticalSpacing={6} fz="sm">
      <Table.Thead>
        <Table.Tr>
          <Table.Th ta="right">Nr</Table.Th>
          <Table.Th>Description</Table.Th>
          {showCovered && <Table.Th ta="center">In installment</Table.Th>}
          <Table.Th ta="center">Qty</Table.Th>
          <Table.Th ta="right">Amount</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>{rows}</Table.Tbody>
    </Table>
  );
}

type InvoiceActionHandlers = Pick<
  InvoiceDetailsModalProps,
  | 'onDownloadPDF'
  | 'onCancelInvoice'
  | 'onSettle'
  | 'onRecordMovement'
  | 'onProvidePaymentConfirmation'
  | 'onMondayOpportunityCreate'
>;

// The printed-invoice document (main area). PDF-aligned for reading consistency.
export function InvoiceDocument({ invoice }: { invoice: InvoiceDetails }) {
  const num = (v: number | null | undefined) => (v != null ? Number(v) : 0);
  // The backend resolves what the invoice is worth; this only formats it. An
  // invoice loaded from a list payload carries no resolved figures, and this
  // screen will not invent them: a breakdown assembled here would state a tax
  // and a discount of its own and disagree with the document. Only the closing
  // figure the payload does carry - already net of any discount, in the
  // currency the client settles in - is shown until the details arrive.
  const money = invoice.money ?? null;
  const discounted = (money?.discountAmount ?? 0) > 0;
  const baseCur = invoice.baseCurrency || invoice.dueCurrency;
  const dueCur = invoice.dueCurrency;
  const converted = !!baseCur && !!dueCur && baseCur !== dueCur;
  const vatPct = num(invoice.vatRate) * 100;
  const vatLabel = `TAX (${vatPct % 1 ? vatPct.toFixed(1) : vatPct.toFixed(0)}%)`;
  // How tax is stated is decided by the issuer, never by the rate, and the
  // backend resolves it once for both surfaces: an issuer that levies no VAT
  // has no rate to show, so the page must not print one where the document
  // does not. An invoice loaded from a list payload carries no decision yet,
  // and is shown as it always was until the details arrive.
  const taxLine = invoice.taxLine ?? { showRate: true, showDash: false };
  const client = invoice.clientInformation;
  const series = invoice.installments ?? null;

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group gap="sm" wrap="nowrap">
          <PiBuildings size={24} />
          <Text fw={700} fz="lg" style={{ wordBreak: 'break-word' }}>
            {invoice.invoicingCompanyName || 'Invoice'}
          </Text>
        </Group>
        {isUpcomingInvoice(invoice) ? (
          <Badge size="lg" variant="light" color="gray">
            Scheduled
          </Badge>
        ) : (
          invoice.partNumber != null &&
          !series && (
            <Badge variant="light" color="indigo">
              Part {invoice.partNumber}
            </Badge>
          )
        )}
      </Group>
      <Divider />

      <Text fw={700} fz="xl" c="blue.7">
        Invoice
      </Text>

      <Group justify="space-between" align="flex-start" wrap="wrap" gap="xl">
        <Box maw={420}>
          <Text fz="xs" c="dimmed" fw={700}>
            BILL TO
          </Text>
          <Text fw={600}>
            {invoice.invoiceRecipient?.billToName ||
              client?.name ||
              invoice.contactName}
          </Text>
          <Text fz="sm">{client?.email || invoice.contactEmail}</Text>
          {client?.address && (
            <Text fz="sm" c="dimmed">
              {client.address}
            </Text>
          )}
          {client?.taxId && (
            <Text fz="sm" c="dimmed">
              Tax ID: {client.taxId}
            </Text>
          )}
        </Box>
        <Stack gap={2} miw={220}>
          <MetaRow label="INVOICE" value={invoice.invoiceNumber} />
          {series && (
            <MetaRow
              label="INSTALLMENT"
              value={`${series.seq} OF ${series.total}`}
            />
          )}
          <MetaRow label="DATE" value={formatDate(invoice.createdAt)} />
          <MetaRow
            label="DUE DATE"
            value={invoice.dueAt ? formatDate(invoice.dueAt) : '—'}
          />
        </Stack>
      </Group>

      {series ? (
        <>
          <InstallmentItemsSection invoice={invoice} />
          <Text fz="sm" c="dimmed">
            This invoice covers installment {series.seq} of {series.total}.
          </Text>
        </>
      ) : (
        <LineItemsSection invoice={invoice} />
      )}

      <Group justify="flex-end">
        <Stack gap={4} miw={280}>
          {money && invoice.subtotalAmount != null && (
            <TotalRow
              label="SUBTOTAL"
              value={money2(num(invoice.subtotalAmount), baseCur)}
            />
          )}
          {/* A discount is recorded apart from the invoice, so the total the
              invoice was issued at is not what the client owes. Both are
              stated, and the closing figure is always the one they pay. */}
          {money && discounted && (
            <TotalRow
              label={`DISCOUNT (${money.discountPercentage}%)`}
              value={`-${money2(money.discountAmount, baseCur)}`}
            />
          )}
          {money && invoice.vatAmount != null && taxLine.showRate && (
            <TotalRow
              label={vatLabel}
              value={money2(money.taxAmount, baseCur)}
            />
          )}
          {money && !taxLine.showRate && taxLine.showDash && (
            <TotalRow label="TAX" value="-" />
          )}
          {money && <Divider />}
          <TotalRow
            label={series ? 'AMOUNT DUE NOW' : 'TOTAL DUE'}
            value={
              money
                ? money2(money.payable, baseCur)
                : money2(num(invoice.dueAmount), dueCur)
            }
            strong
          />
          {money && converted && (
            <Text fz="xs" c="dimmed" ta="right">
              Amount due {money2(money.amountDue, dueCur)}
            </Text>
          )}
        </Stack>
      </Group>
      {invoice.vatNote && (
        <Text fz="xs" c="dimmed">
          {invoice.vatNote}
        </Text>
      )}
    </Stack>
  );
}

function DeliverySection({ invoice }: { invoice: InvoiceDetails }) {
  const send = useSendInvoice();
  const download = useDownloadInvoicePDF();
  const delivery = invoice.delivery;
  const version = invoice.documentVersion ?? 1;

  // Amending never sends. This block is the only thing standing between a
  // corrected invoice and a client still paying against the old one, so it is
  // always shown - including the reassuring case.
  const state = delivery?.state ?? 'never_sent';
  const outdated = state === 'outdated_at_client';

  const paymentDetailsMoved =
    outdated &&
    (invoice.amendments ?? []).some((a) => {
      const before = a.oldValues ?? {};
      const after = a.newValues ?? {};
      return (
        before.invoicingCompanyId !== after.invoicingCompanyId ||
        before.paymentMethod !== after.paymentMethod
      );
    });

  return (
    <Section title="Delivery" icon={<PiPaperPlaneTilt size={14} />}>
      <Stack gap="xs">
        {outdated && (
          <Alert color="orange" icon={<PiWarning size={14} />} p="xs">
            <Text size="xs" fw={600}>
              The client holds version {delivery?.sentVersion}; this invoice is
              at version {version}.
            </Text>
            {paymentDetailsMoved && (
              <Text size="xs" mt={4}>
                The payment details changed. Until this is sent, the client may
                pay to the wrong account.
              </Text>
            )}
          </Alert>
        )}

        {state === 'sent' && (
          <Text size="xs" c="teal">
            Sent {formatDateTime(delivery?.sentAt)} to {delivery?.sentTo}{' '}
            (version {delivery?.sentVersion}, current)
          </Text>
        )}

        {state === 'never_sent' && (
          <Text size="xs" c="dimmed">
            Not yet sent to the client.
          </Text>
        )}

        {outdated && (
          <Text size="xs" c="dimmed">
            Last sent {formatDateTime(delivery?.sentAt)} to {delivery?.sentTo}
          </Text>
        )}

        <Group gap="xs">
          <Button
            size="xs"
            variant="default"
            leftSection={<PiDownloadSimple size={12} />}
            onClick={() => download.mutate(invoice.id.toString())}
          >
            Download PDF
          </Button>
          <Button
            size="xs"
            color={outdated ? 'orange' : undefined}
            variant={state === 'sent' ? 'default' : 'filled'}
            leftSection={<PiPaperPlaneTilt size={12} />}
            loading={send.isPending}
            onClick={() => send.mutate({ id: Number(invoice.id) })}
          >
            {state === 'sent' ? 'Send again' : 'Send invoice to client'}
          </Button>
        </Group>

        {(invoice.deliveries?.length ?? 0) > 0 && (
          <Stack gap={2} mt="xs">
            <Text size="xs" fw={600} c="dimmed">
              History
            </Text>
            {invoice.deliveries!.map((d) => (
              <Text key={d.id} size="xs" c={d.succeeded ? 'dimmed' : 'red'}>
                {formatDateTime(d.sentAt)} · v{d.documentVersion} ·{' '}
                {d.recipientEmail}
                {d.sentByName ? ` · ${d.sentByName}` : ''}
                {d.succeeded ? '' : ` · failed: ${d.error ?? 'unknown'}`}
              </Text>
            ))}
          </Stack>
        )}
      </Stack>
    </Section>
  );
}

// Operational / utility sections (sidebar): related invoices, status history,
// part amendment and the action buttons. Not part of the printed invoice.
export function InvoiceUtilities({
  invoice,
  offerId,
  ...handlers
}: { invoice: InvoiceDetails; offerId?: number } & InvoiceActionHandlers) {
  // A scheduled (not-yet-issued) installment is a read-only preview. A mock
  // installment has no backend record, so real actions are replaced by a mock
  // "mark paid" (the synthetic id can't be sent to the API).
  const upcoming = isUpcomingInvoice(invoice);
  const mock = isMockInvoice(invoice);
  return (
    <>
      <Stack gap="md">
        {/* What the reader came to do leads the sidebar; the record they came to read
            follows it. Burying the actions under payment, payer and history meant
            scrolling past everything to reach the one control that was wanted. */}
        {!upcoming && !mock && (
          <StatusSpecificActions invoice={invoice} {...handlers} />
        )}

        {!upcoming && mock && (
          <MockInstallmentActions invoice={invoice} offerId={offerId} />
        )}

        <OfferContextSection invoice={invoice} />

        <Section title="Payment" icon={<PiMoney size={14} />}>
          <PaymentInfo invoice={invoice} />
        </Section>

        <PayerCard invoice={invoice} />

        {!upcoming && !mock && <DeliverySection invoice={invoice} />}

        {!upcoming && !mock && invoice.status !== InvoiceStatus.ISSUED && (
          <Section title="Status History" icon={<PiCalendar size={14} />}>
            <StatusHistory invoice={invoice} />
          </Section>
        )}
      </Stack>
    </>
  );
}

// Mock installment invoices have no backend record; payment marking happens in
// the Zustand schedule store instead of the API.
function MockInstallmentActions({
  invoice,
  offerId,
}: {
  invoice: InvoiceDetails;
  offerId?: number;
}) {
  const schedules = useMockSplitSchedule((s) => s.schedules);
  const markPaid = useMockSplitSchedule((s) => s.markInstallmentPaid);
  if (!offerId || invoice.partNumber == null) return null;
  const inst = schedules[offerId]?.installments[invoice.partNumber - 1];
  if (!inst) return null;

  if (inst.paid) {
    return (
      <Text fz="sm" c="teal">
        Payment recorded (mock).
      </Text>
    );
  }
  return (
    <Group>
      <Button
        color="green"
        leftSection={<PiCheck size={16} />}
        onClick={() => markPaid(offerId, inst.id)}
      >
        Confirm payment to mark paid
      </Button>
    </Group>
  );
}

// Single-column composition for the drawer.
export function InvoiceDetailsBody({
  invoice,
  offerId,
  ...handlers
}: { invoice: InvoiceDetails; offerId?: number } & InvoiceActionHandlers) {
  return (
    <Stack gap="md">
      <InvoiceDocument invoice={invoice} />
      <Divider />
      <InvoiceUtilities invoice={invoice} offerId={offerId} {...handlers} />
    </Stack>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <Group justify="space-between" gap="xl" wrap="nowrap">
      <Text fz="xs" c="dimmed" fw={600}>
        {label}
      </Text>
      <Text fz="sm">{value}</Text>
    </Group>
  );
}

function TotalRow({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <Group justify="space-between" gap="xl" wrap="nowrap">
      <Text fz={strong ? 'sm' : 'xs'} c="dimmed" fw={strong ? 700 : 600}>
        {label}
      </Text>
      <Text fz={strong ? 'md' : 'sm'} fw={strong ? 700 : 500}>
        {value}
      </Text>
    </Group>
  );
}

export function InvoiceDetailsModal({
  invoiceId,
  opened,
  onClose,
  offerId,
  invoiceOverride,
  ...handlers
}: InvoiceDetailsModalProps) {
  const fetched = useInvoiceDetails(invoiceId || '');
  const invoice = invoiceOverride ?? fetched.data;
  const isLoading = invoiceOverride ? false : fetched.isLoading;
  const error = invoiceOverride ? null : fetched.error;

  if (!invoiceId && !invoiceOverride) {
    return null;
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="65%"
      title="Invoice details"
      styles={{ content: { maxWidth: 1200 } }}
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
    >
      <LoadingOverlay visible={isLoading} />
      {error && (
        <Alert color="red" icon={<PiInfo />} mb="sm" p="xs">
          <Text size="xs">Failed to load invoice details.</Text>
        </Alert>
      )}
      {invoice && (
        <InvoiceDetailsBody invoice={invoice} offerId={offerId} {...handlers} />
      )}
    </Drawer>
  );
}

export default InvoiceDetailsModal;
